import React, { useContext, useState } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { FiMessageSquare, FiAlertCircle, FiCreditCard, FiAlertTriangle } from "react-icons/fi";
import {
  Booking,
  BookingRemark,
  IBookingSalesAndPaymentsResult,
  ProductCompleted,
  ProductSaleItem,
  ProductTypes,
  RemarkType,
} from "../../../../logic/api";
import { SessionContext } from "../../../../utility/useSession";
import { formatCurrency } from "../../../../utility/utils";
import { usePackage } from "../../../../hooks/usePackage";
import { useProductsCompleted } from "../../../../hooks/useProductsCompleted";
import ProductLozenge from "./ProductLozenge";
import HeaderInfoBadge from "./HeaderInfoBadge";
import ProductCompletedDialog from "./ProductCompletedDialog";
import { useTranslation } from "react-i18next";
import { ReleaseFlag, useReleaseManagement } from "@/utility/feature-management";

interface BookingHeaderPanelProps {
  booking: Booking;
  saleItems: ProductSaleItem[];
  salesAndPayments: IBookingSalesAndPaymentsResult;
  remarks: BookingRemark[];
  bookingId: number;
  onCompletedListUpdated: () => void;
  remarksSectionRef: React.RefObject<HTMLDivElement>;
  paymentsSectionRef: React.RefObject<HTMLDivElement>;
  onRemarkTypeChange: (type: RemarkType) => void;
  onShowComplaints: () => void;
  readOnly?: boolean;
}

const HIDDEN_PRODUCT_TYPES = [ProductTypes.additionalDay, ProductTypes.overstay];
const SERVICEABLE_PRODUCT_TYPES = [ProductTypes.electricVehicle, ProductTypes.valet];

const BookingHeaderPanel: React.FC<BookingHeaderPanelProps> = ({
  booking,
  saleItems,
  salesAndPayments,
  remarks,
  bookingId,
  onCompletedListUpdated,
  remarksSectionRef,
  paymentsSectionRef,
  onRemarkTypeChange,
  onShowComplaints,
  readOnly = false,
}) => {
  const isCompletedProductsFixEnabled = useReleaseManagement(ReleaseFlag.releaseCompletedProductsFix);

  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const { data: packageData, isLoading: isPackageLoading, isError: isPackageError } = usePackage({
    packageCode: booking.packageCode,
    packageRef: booking.packageRef,
    carParkId: booking.carPark?.id,
  });
  const { data: productsCompleted, isLoading: isProductsCompletedLoading } = useProductsCompleted(bookingId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSaleItem, setSelectedSaleItem] = useState<ProductSaleItem | null>(null);
  const [selectedCompletedRecord, setSelectedCompletedRecord] = useState<ProductCompleted | null>(null);

  // filter out voided items and hidden product types
  const visibleItems = saleItems.filter(
    (item) => !item.voided && !HIDDEN_PRODUCT_TYPES.includes(item.productType)
  );

  const inPackageProducts = visibleItems.filter(
    (item) => booking.packageCode && item.packageCode === booking.packageCode
  );
  const outOfPackageProducts = visibleItems.filter(
    (item) => !booking.packageCode || item.packageCode !== booking.packageCode
  );

  // Badges visibility
  const hasCustomerNote = remarks.some(
    (remark) => remark.remarkType === RemarkType.Customer && !remark.deletedAt
  );
  const hasStaffNote = remarks.some(
    (remark) => remark.remarkType === RemarkType.Internal && !remark.deletedAt
  );
  const hasOutstandingBalance = salesAndPayments.outstanding > 0;
  const hasComplaint = booking.hasComplaint === true;

  const getCompletedRecord = (item: ProductSaleItem): ProductCompleted | null => {
    if (!productsCompleted) return null;

    if (isCompletedProductsFixEnabled) {
      return productsCompleted?.find(pc => {
        if (pc.productSaleItem) {
          return (pc.productSaleItem?.id === item.id && pc.productType === item.productType);
        }

        return (pc.productType === item.productType);
      }) ?? null;
    }

    return productsCompleted.find((pc) => pc.productType === item.productType) ?? null;

  };

  const handleLozengeClick = (item: ProductSaleItem, completedRecord: ProductCompleted | null) => {
    setSelectedSaleItem(item);
    setSelectedCompletedRecord(completedRecord);
    setDialogOpen(true);
  };

  const handleDialogCompleted = () => {
    onCompletedListUpdated();
  };

  const scrollToRemarks = (type: RemarkType) => {
    onRemarkTypeChange(type);
    remarksSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToPayments = () => {
    paymentsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderProducts = (products: ProductSaleItem[]) =>
    products.map((item) => {
      const isServiceableType = SERVICEABLE_PRODUCT_TYPES.includes(item.productType);
      const completedRecord = getCompletedRecord(item);
      const isCompleted = !!completedRecord;
      // Can view completed record (any mode) OR create new (this is for own car park only)
      const clickable = isServiceableType && (
        isCompleted || (!readOnly && completedRecord === null && productsCompleted != null)
      );
      return (
        <ProductLozenge
          key={item.id}
          saleItem={item}
          isServiceable={isServiceableType}
          isCompleted={isCompleted}
          isLoading={isServiceableType && isProductsCompletedLoading}
          onClick={clickable ? () => handleLozengeClick(item, completedRecord) : undefined}
        />
      );
    });

  return (
    <>
      <Box data-testid="booking-header-panel" sx={{
        display: "flex",
        flexWrap: { xs: "wrap", md: "nowrap" },
        alignItems: "flex-start",
        borderRadius: 1,
        bgcolor: "white",
        border: "2px solid #eee",
        p: "12px 16px",
        gap: 1,
        animation: "fadeInUp 400ms cubic-bezier(0.4, 0, 0.2, 1) backwards",
        "@keyframes fadeInUp": {
          from: { opacity: 0, transform: "translateY(12px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}>
        <Box data-testid="booking-header-products" sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "flex-start", gap: 1, flex: "1 1 0%", minWidth: 0 }}>
          {isPackageLoading && (booking.packageCode || booking.packageRef) ? (
            <Box sx={{ width: "100%" }}>
              <Skeleton variant="text" width={140} sx={{ fontSize: "0.95rem", lineHeight: 1.5 }} />
            </Box>
          ) : packageData?.name ? (
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", width: "100%", textAlign: "left", lineHeight: 1.5 }}>
              {packageData.name}
            </Typography>
          ) : (booking.packageCode || booking.packageRef) && isPackageError ? (
            <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", width: "100%", textAlign: "left", color: "#999", fontStyle: "italic", lineHeight: 1.5 }}>
              {t("booking.packageUnavailable")}
            </Typography>
          ) : null}
          {renderProducts(inPackageProducts)}
          {inPackageProducts.length > 0 && outOfPackageProducts.length > 0 && (
            <Box
              sx={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: "#888",
                mx: 0.5,
                alignSelf: "center",
                flexShrink: 0,
              }}
            />
          )}
          {renderProducts(outOfPackageProducts)}
        </Box>
        <Box data-testid="booking-header-badges" sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: { xs: "flex-start", md: "flex-end" },
          gap: 0.75,
          ml: { xs: 0, md: "auto" },
          pl: { xs: 0, md: 1.5 },
          mt: { xs: 1, md: 0 },
          flex: { xs: "1 0 100%", md: "0 0 auto" },
        }}>
          {hasComplaint && (
            <HeaderInfoBadge
              label={t("booking.headerBadges.complaint")}
              backgroundColor="#fce4ec"
              textColor="#c62828"
              icon={<FiAlertTriangle size={14} />}
              onClick={readOnly ? undefined : onShowComplaints}
            />
          )}
          {hasCustomerNote && (
            <HeaderInfoBadge
              label={t("booking.headerBadges.customerNote")}
              backgroundColor="#e3f2fd"
              textColor="#1565c0"
              icon={<FiMessageSquare size={14} />}
              onClick={readOnly ? undefined : () => scrollToRemarks(RemarkType.Customer)}
            />
          )}
          {hasStaffNote && (
            <HeaderInfoBadge
              label={t("booking.headerBadges.staffNote")}
              backgroundColor="#fff3e0"
              textColor="#e65100"
              icon={<FiAlertCircle size={14} />}
              onClick={readOnly ? undefined : () => scrollToRemarks(RemarkType.Internal)}
            />
          )}
          {hasOutstandingBalance && (
            <HeaderInfoBadge
              label={formatCurrency(salesAndPayments.outstanding, session) as string}
              backgroundColor="#fce4ec"
              textColor="#c62828"
              icon={<FiCreditCard size={14} />}
              onClick={readOnly ? undefined : scrollToPayments}
            />
          )}
        </Box>
      </Box>

      <ProductCompletedDialog
        open={dialogOpen}
        productSaleItem={selectedSaleItem}
        bookingId={bookingId}
        existingRecord={selectedCompletedRecord}
        onClose={() => setDialogOpen(false)}
        onCompleted={handleDialogCompleted}
      />
    </>
  );
};

export default BookingHeaderPanel;
